import { formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  UserEditLog,
  UserEditLogChange,
  USER_EDIT_LOG_EVENT_FIELDS,
  USER_EDIT_LOG_MASKED_FIELDS,
} from '../../../models/UserEditLog';
import { PaginatedResult } from '../../model/paginated-result';
import { UserService } from '../../services/user.service';

/**
 * Modal com o histórico de edições de uma conta — quem alterou, quando e quais
 * campos. Alimentado por GET /users/{id}/edit-logs, restrito a administrador.
 *
 * Aberto pelo botão "Histórico de edições" da tela de edição de usuário, que
 * chama `open(id, label)`:
 *
 *   <app-user-edit-logs #editLogs></app-user-edit-logs>
 *   <button (click)="editLogs.open(user.id, user.name)">…</button>
 */
@Component({
  selector: 'app-user-edit-logs',
  templateUrl: './user-edit-logs.component.html',
  styleUrls: ['./user-edit-logs.component.scss']
})
export class UserEditLogsComponent implements OnDestroy {
  /**
   * Colunas que saíram da tela e não aparecem mais no histórico, nem nas entradas antigas que
   * já as gravaram (CU-17tm466qj38): `whatsapp_connect` era o WhatsApp antigo por QR, que o
   * backend não grava mais. Sem isto a entrada antiga cairia no humanize ("Whatsapp Connect").
   */
  static readonly HIDDEN_FIELDS: readonly string[] = ['whatsapp_connect'];

  private static readonly ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

  @ViewChild('editLogsModal') modalTpl!: TemplateRef<unknown>;

  userId: string | null = null;
  userLabel = '';
  result: PaginatedResult<UserEditLog[]> | null = null;
  perPage = 15;
  loading = false;
  error: string | null = null;

  private modalRef: NgbModalRef | null = null;
  /**
   * Uma request de cada vez: paginar cancela a anterior e o array de
   * assinaturas concluídas deixa de crescer a cada reabertura do modal.
   */
  private sub: Subscription | null = null;

  constructor(
    private modalService: NgbModal,
    private userService: UserService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    // O modal vive no body, fora da view do pai: sem isto ele fica órfão na
    // tela quando o componente que o hospeda é destruído com ele aberto.
    this.modalRef?.dismiss();
  }

  open(userId: string, userLabel: string = ''): void {
    // Trocar de conta pela rota (/users/edit/A -> /users/edit/B) reusa o mesmo
    // componente, sem ngOnDestroy: sem isto o modal anterior continuaria aberto
    // atrás do novo.
    this.modalRef?.dismiss();

    this.userId = userId;
    this.userLabel = userLabel;
    this.result = null;
    this.error = null;

    this.modalRef = this.modalService.open(this.modalTpl, {
      size: 'lg',
      ariaLabelledBy: 'editLogsTitle',
      scrollable: true,
    });

    const ref = this.modalRef;
    // `result` rejeita no dismiss (ESC/backdrop); o catch evita o unhandled
    // rejection. Só limpa se ainda for este modal — reabrir antes do fechamento
    // anterior resolver não pode zerar a referência do modal novo.
    ref.result
      .catch(() => undefined)
      .then(() => {
        if (this.modalRef === ref) {
          this.modalRef = null;
        }
      });

    this.load(1);
  }

  load(page: number): void {
    if (!this.userId) {
      return;
    }

    // Antes do `loading = true`: `finalize` também dispara no unsubscribe e
    // desligaria o spinner que acabamos de ligar.
    this.sub?.unsubscribe();

    this.loading = true;
    this.error = null;

    // finalize garante o reset do spinner mesmo se o componente for destruído
    // com a request em voo.
    this.sub = this.userService
      .listEditLogs(this.userId, page, this.perPage)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => (this.result = this.withoutHiddenFields(result)),
        error: () => (this.error = 'Não foi possível carregar o histórico de edições.'),
      });
  }

  /**
   * Tira as colunas de `HIDDEN_FIELDS` de cada entrada; a entrada que só tinha elas some da
   * página (o `total` da paginação continua o do backend).
   */
  private withoutHiddenFields(result: PaginatedResult<UserEditLog[]>): PaginatedResult<UserEditLog[]> {
    const hidden = UserEditLogsComponent.HIDDEN_FIELDS;
    const data = (result?.data ?? [])
      .map((log) => ({ ...log, changed_fields: (log.changed_fields ?? []).filter((c) => hidden.indexOf(c.field) === -1) }))
      .filter((log) => log.changed_fields.length > 0);

    return { ...result, data };
  }

  /**
   * Campos sensíveis chegam com old/new nulos de propósito — a trilha registra
   * que mudaram, nunca o valor. Ver SENSITIVE_FIELDS em UserObserver.
   */
  isMaskedField(field: string): boolean {
    return USER_EDIT_LOG_MASKED_FIELDS.indexOf(field) !== -1;
  }

  /**
   * O que a tela usa. Além da lista, trata como mascarada qualquer coluna real
   * com `old` e `new` nulos: o observer só grava coluna que mudou de valor, então
   * nulo → nulo só existe quando o backend escondeu o valor. Assim um campo que
   * o backend passou a mascarar aparece com cadeado mesmo antes de a lista
   * acima ser atualizada. Eventos sintéticos ficam de fora — neles os dois nulos
   * são normais (exclusão/reativação).
   */
  isMasked(change: UserEditLogChange): boolean {
    if (this.isMaskedField(change.field)) {
      return true;
    }

    return !this.isEventField(change.field) && change.old == null && change.new == null;
  }

  /**
   * Afiliação e ciclo de vida da conta são eventos, não troca de valor — sem
   * isto apareciam como uma edição vazia ("— → —").
   */
  isEventField(field: string): boolean {
    return USER_EDIT_LOG_EVENT_FIELDS.indexOf(field) !== -1;
  }

  /** O afilhado envolvido (quando há); exclusão/reativação não têm detalhe. */
  eventDetail(change: UserEditLogChange): string {
    return change.new ?? change.old ?? '';
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    // Colunas `datetime` chegam em ATOM (UserObserver::normalize); mesmo formato
    // e fuso local da data do cabeçalho, em vez da string crua em UTC.
    if (typeof value === 'string' && UserEditLogsComponent.ISO_DATETIME.test(value)) {
      return formatDate(value, 'dd/MM/yyyy, HH:mm:ss', this.locale);
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  trackByLogId(_index: number, log: UserEditLog): string {
    return log.id;
  }
}
